import type { UndefinedOnPartialDeep } from 'type-fest';

import ListView, { ListViewItem } from '@ingka/list-view';
import SSRIcon from '@ingka/ssr-icon';
import drill from '@ingka/ssr-icon/paths/drill';
import pin from '@ingka/ssr-icon/paths/location-pin';
import Text from '@ingka/text';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { translate } from '@repo/phrase';
import { assetTypes } from '@repo/types/asset';
import type { Service } from '@repo/types/service';

import { Feature } from '@/components/feature';

import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { formatDeliveryAddress, getEstimatedServiceArrivalTime } from '@/utils/services.utils';

import parentStyles from '../styles.module.scss';
import styles from './styles.module.scss';

interface Props {
    service: UndefinedOnPartialDeep<Service>;
}

export const ArticleServiceView = ({ service }: Props) => {
    const { timeWindow, isDelivery } = service;
    const { assetType, assetId } = useAssetParams();
    const { data } = useAsset(assetType, assetId);
    const isOrder = assetType === assetTypes.order;

    const estimatedServiceArrivalTime = getEstimatedServiceArrivalTime(timeWindow, isDelivery);
    const deliveryContact = selectDeliveryContact(data);

    const serviceAddress = deliveryContact
        ? formatDeliveryAddress(deliveryContact)
        : translate('services_article_service_view_service_address_not_found');

    return (
        <Feature configPath={['articleServiceView.enable']}>
            <div className={styles.article_service_view_container} data-testid="article-service-view">
                <ListView id="article-service-header" size="medium">
                    <ListViewItem
                        title={service.serviceName ?? ''}
                        inset={true}
                        emphasised
                        description={
                            isOrder ? (
                                <>
                                    <span data-testid="article-service-estimated-arrival-time">
                                        {estimatedServiceArrivalTime}
                                    </span>
                                    <div className={styles.article_service_view_status_box}>
                                        <Text data-testid="article-service-status-text">{service.statusText}</Text>
                                    </div>
                                </>
                            ) : undefined
                        }
                        data-testid="article-service-view-header"
                    />
                </ListView>
                <div className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}>
                    <SSRIcon paths={pin} data-testid="article-service-address-icon-pin" />
                    <Text data-testid="article-service-address">{serviceAddress}</Text>
                </div>
                {isOrder && (
                    <div className={`${parentStyles.article_view_box} ${parentStyles.article_list_container}`}>
                        <SSRIcon paths={drill} data-testid="article-service-provider-icon-drill" />
                        <Text data-testid="article-service-provider-name">{service.serviceProviderName}</Text>
                    </div>
                )}
            </div>
        </Feature>
    );
};
